"use strict";
var __makeTemplateObject = (this && this.__makeTemplateObject) || function (cooked, raw) {
    if (Object.defineProperty) { Object.defineProperty(cooked, "raw", { value: raw }); } else { cooked.raw = raw; }
    return cooked;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.CalendarHeading = exports.RangeCalendarCell = exports.CalendarCell = exports.CalendarGrid = exports.RangeCalendar = exports.CalendarHeader = exports.Calendar = void 0;
var styled_1 = __importDefault(require("@emotion/styled"));
var react_aria_components_1 = require("react-aria-components");
var theme_1 = require("../../theme");
/**
 * A calendar component that allows users to select a single date.
 *
 * @see {@link https://react-spectrum.adobe.com/react-aria/Calendar.html}
 * @see {@link https://compass.circleci.com/?path=/story/rac-calendar--default}
 *
 * @example
 * ```tsx
 * <Calendar aria-label="Appointment date">
 *   <CalendarHeader>
 *     <IconButton slot="previous"><ArrowLeftIcon /></IconButton>
 *     <CalendarHeading />
 *     <IconButton slot="next"><ArrowRightIcon /></IconButton>
 *   </CalendarHeader>
 *   <CalendarGrid>{(date) => <CalendarCell date={date} />}</CalendarGrid>
 * </Calendar>
 * ```
 *
 * @param value - The current date value. Defaults to null
 * @param defaultValue - The default date value. Defaults to null
 * @param isDisabled - Whether the calendar is disabled. Defaults to false
 * @param isReadOnly - Whether the calendar is read only. Defaults to false
 * @param isRequired - Whether the calendar is required. Defaults to false
 * @param minValue - The minimum allowed date value. Defaults to null
 * @param maxValue - The maximum allowed date value. Defaults to null
 * @param onChange - Handler that is called when the date changes
 * @param children - The calendar header and grid components to display
 */
exports.Calendar = (0, styled_1.default)(react_aria_components_1.Calendar)(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n  width: fit-content;\n  max-width: 100%;\n  color: ", ";\n\n  & table {\n    border-collapse: collapse;\n\n    & td {\n      padding: ", " 0;\n    }\n  }\n"], ["\n  width: fit-content;\n  max-width: 100%;\n  color: ", ";\n\n  & table {\n    border-collapse: collapse;\n\n    & td {\n      padding: ", " 0;\n    }\n  }\n"])), theme_1.theme.colors.n900, theme_1.theme.space.space2);
/**
 * A container component for the calendar header that holds navigation buttons and the calendar heading.
 *
 * @example
 * ```tsx
 * <CalendarHeader>
 *   <IconButton slot="previous"><ArrowLeftIcon /></IconButton>
 *   <CalendarHeading />
 *   <IconButton slot="next"><ArrowRightIcon /></IconButton>
 * </CalendarHeader>
 * ```
 *
 * @param children - The navigation buttons and heading components to display
 */
exports.CalendarHeader = styled_1.default.div(templateObject_2 || (templateObject_2 = __makeTemplateObject(["\n  display: flex;\n  align-items: center;\n  margin: 0 0 ", " 0;\n"], ["\n  display: flex;\n  align-items: center;\n  margin: 0 0 ", " 0;\n"])), theme_1.theme.space.space8);
/**
 * A calendar component that allows users to select a date range.
 *
 * @example
 * ```tsx
 * <RangeCalendar aria-label="Appointment date range">
 *   <CalendarHeader>
 *     <IconButton slot="previous"><ArrowLeftIcon /></IconButton>
 *     <CalendarHeading />
 *     <IconButton slot="next"><ArrowRightIcon /></IconButton>
 *   </CalendarHeader>
 *   <CalendarGrid>{(date) => <RangeCalendarCell date={date} />}</CalendarGrid>
 * </RangeCalendar>
 * ```
 *
 * @param value - The current date range value. Defaults to null
 * @param defaultValue - The default date range value. Defaults to null
 * @param isDisabled - Whether the calendar is disabled. Defaults to false
 * @param isReadOnly - Whether the calendar is read only. Defaults to false
 * @param isRequired - Whether the calendar is required. Defaults to false
 * @param minValue - The minimum allowed date value. Defaults to null
 * @param maxValue - The maximum allowed date value. Defaults to null
 * @param onChange - Handler that is called when the date range changes
 * @param children - The calendar header and grid components to display
 */
exports.RangeCalendar = (0, styled_1.default)(react_aria_components_1.RangeCalendar)(templateObject_3 || (templateObject_3 = __makeTemplateObject(["\n  width: fit-content;\n  max-width: 100%;\n  color: ", ";\n\n  header {\n    display: flex;\n    align-items: center;\n    margin: 0 0 ", " 0;\n  }\n\n  & table {\n    border-collapse: collapse;\n\n    & td {\n      padding: ", " 0;\n    }\n  }\n"], ["\n  width: fit-content;\n  max-width: 100%;\n  color: ", ";\n\n  header {\n    display: flex;\n    align-items: center;\n    margin: 0 0 ", " 0;\n  }\n\n  & table {\n    border-collapse: collapse;\n\n    & td {\n      padding: ", " 0;\n    }\n  }\n"])), theme_1.theme.colors.n900, theme_1.theme.space.space8, theme_1.theme.space.space2);
/**
 * A grid component that displays the calendar days.
 * Must be used within a Calendar or RangeCalendar component.
 *
 * @example
 * ```tsx
 * <CalendarGrid>{(date) => <CalendarCell date={date} />}</CalendarGrid>
 * ```
 *
 * @param children - A function to render calendar cells for each date in the month
 */
var react_aria_components_2 = require("react-aria-components");
Object.defineProperty(exports, "CalendarGrid", { enumerable: true, get: function () { return react_aria_components_2.CalendarGrid; } });
/**
 * A cell component that represents a single day in the calendar.
 * Must be used within a CalendarGrid component.
 *
 * @example
 * ```tsx
 * <CalendarCell date={date} />
 * ```
 *
 * @param date - The date to display in this cell
 */
exports.CalendarCell = (0, styled_1.default)(react_aria_components_1.CalendarCell)(templateObject_4 || (templateObject_4 = __makeTemplateObject(["\n  width: ", ";\n  height: ", ";\n  align-content: center;\n  text-align: center;\n  border-radius: ", ";\n  cursor: default;\n  outline: none;\n  forced-color-adjust: none;\n\n  &[data-outside-month] {\n    display: none;\n  }\n\n  &[data-hovered] {\n    background: ", ";\n  }\n\n  &[data-pressed] {\n    background: ", ";\n  }\n\n  &[data-focus-visible] {\n    outline: 2px solid ", ";\n    outline-offset: 2px;\n  }\n\n  &[data-selected] {\n    background: ", ";\n    color: ", ";\n  }\n\n  &[data-disabled] {\n    color: ", ";\n    opacity: ", ";\n    cursor: not-allowed;\n  }\n\n  &[data-unavailable] {\n    color: ", ";\n    text-decoration: line-through;\n  }\n"], ["\n  width: ", ";\n  height: ", ";\n  align-content: center;\n  text-align: center;\n  border-radius: ", ";\n  cursor: default;\n  outline: none;\n  forced-color-adjust: none;\n\n  &[data-outside-month] {\n    display: none;\n  }\n\n  &[data-hovered] {\n    background: ", ";\n  }\n\n  &[data-pressed] {\n    background: ", ";\n  }\n\n  &[data-focus-visible] {\n    outline: 2px solid ", ";\n    outline-offset: 2px;\n  }\n\n  &[data-selected] {\n    background: ", ";\n    color: ", ";\n  }\n\n  &[data-disabled] {\n    color: ", ";\n    opacity: ", ";\n    cursor: not-allowed;\n  }\n\n  &[data-unavailable] {\n    color: ", ";\n    text-decoration: line-through;\n  }\n"])), theme_1.theme.space.space40, theme_1.theme.space.space40, theme_1.theme.radii.circle, theme_1.theme.colors.n60, theme_1.theme.colors.n70, theme_1.theme.colors.borderPrimary, theme_1.theme.colors.actionPrimary, theme_1.theme.colors.contentInverse, theme_1.theme.colors.n400, theme_1.theme.opacity.semiOpaque, theme_1.theme.colors.n400);
/**
 * A cell component specifically for range selection in the calendar.
 * Extends CalendarCell with additional styling for range selection states.
 * Must be used within a CalendarGrid component inside a RangeCalendar.
 *
 * @example
 * ```tsx
 * <RangeCalendarCell date={date} />
 * ```
 *
 * @param date - The date to display in this cell
 */
exports.RangeCalendarCell = (0, styled_1.default)(exports.CalendarCell)(templateObject_5 || (templateObject_5 = __makeTemplateObject(["\n  &[data-selected] {\n    border-radius: 0;\n  }\n\n  &[data-selection-start] {\n    border-start-start-radius: ", ";\n    border-end-start-radius: ", ";\n  }\n\n  &[data-selection-end] {\n    border-start-end-radius: ", ";\n    border-end-end-radius: ", ";\n  }\n"], ["\n  &[data-selected] {\n    border-radius: 0;\n  }\n\n  &[data-selection-start] {\n    border-start-start-radius: ", ";\n    border-end-start-radius: ", ";\n  }\n\n  &[data-selection-end] {\n    border-start-end-radius: ", ";\n    border-end-end-radius: ", ";\n  }\n"])), theme_1.theme.radii.circle, theme_1.theme.radii.circle, theme_1.theme.radii.circle, theme_1.theme.radii.circle);
/**
 * A heading component that displays the current month and year in the calendar.
 * Must be used within a CalendarHeader component.
 *
 * @example
 * ```tsx
 * <CalendarHeading />
 * ```
 */
exports.CalendarHeading = (0, styled_1.default)(react_aria_components_1.Heading)(templateObject_6 || (templateObject_6 = __makeTemplateObject(["\n  flex: 1;\n  margin: 0;\n  text-align: center;\n  font-size: ", ";\n  line-height: ", ";\n  font-weight: ", ";\n"], ["\n  flex: 1;\n  margin: 0;\n  text-align: center;\n  font-size: ", ";\n  line-height: ", ";\n  font-weight: ", ";\n"])), theme_1.theme.fontSizes.h5, theme_1.theme.lineHeights.h5, theme_1.theme.fontWeights.semiBold);
var templateObject_1, templateObject_2, templateObject_3, templateObject_4, templateObject_5, templateObject_6;
//# sourceMappingURL=index.js.map